import { defineStore } from 'pinia';
// @ts-expect-error no types package available
import nuxtStorage from 'nuxt-storage';
import type { ClientTableColumn, CustomField, Rollout, User } from '@/types';
import { klona } from 'klona';
import {
  defaultClientTableColumnKeys,
  getDefaultClientTableColums,
  type DefaultClientTableColumnKey,
} from './static/client-default-table-columns';

export const sixMonthsInDays = 183;

export const useUserStore = defineStore('user', {
  state: () => {
    return {
      user: undefined as User | undefined,
      fetchedClientTableCustomFields: undefined as CustomField[] | undefined,

      accessToken: null as string | null,
      refreshToken: null as string | null,
    };
  },

  getters: {
    availableRollouts: (state) => {
      const sortedRollouts = klona(state?.user?.rollouts);
      return (
        sortedRollouts?.sort(
          (a, b) =>
            new Date(b.created).getTime() - new Date(a.created).getTime()
        ) ?? []
      );
    },
  },

  actions: {
    setRememberMe(hasRememberMe: boolean) {
      nuxtStorage.localStorage.setData(
        'rememberMe',
        hasRememberMe,
        sixMonthsInDays,
        'd'
      );
    },
    setAccessToken(accessToken: string) {
      nuxtStorage.localStorage.setData('accessToken', accessToken, 1, 'd');
      this.accessToken = accessToken;
    },
    setRefreshToken(refreshToken?: string) {
      nuxtStorage.localStorage.setData(
        'refreshToken',
        refreshToken,
        sixMonthsInDays,
        'd'
      );
      this.refreshToken = refreshToken ?? null;
    },
    async refreshUserData() {
      const user = await fetchCurrentUser();
      if (user) {
        this.user = user;
      }
    },
    async fetchClientTableCustomFields() {
      const { $client } = useNuxtApp();
      const { selectedRollout } = useRolloutRouting();

      try {
        const rolloutId = (selectedRollout.value as Rollout)?._id;
        if (this.user?.settings?.clientTableColumns?.[rolloutId]) {
          const customFieldsToFetch = this.user.settings.clientTableColumns[
            rolloutId
          ].filter(
            (e) =>
              !defaultClientTableColumnKeys.includes(
                e.fieldId as DefaultClientTableColumnKey
              )
          );

          if (customFieldsToFetch.length) {
            this.fetchedClientTableCustomFields = (
              await $client.customField.getByIds.query({
                ids: customFieldsToFetch.map((f) => f.fieldId),
              })
            ).items as CustomField[];
          } else {
            this.fetchedClientTableCustomFields = [];
          }
        } else {
          this.fetchedClientTableCustomFields = [];
        }
      } catch (error) {
        useErrorHandler(error);
      }
    },
    getClientTableColumns(i18n: ReturnType<typeof useI18n>) {
      if (!this.fetchedClientTableCustomFields) {
        // We need the fetched custom fields to be initialized
        return null;
      }

      const { selectedRollout } = useRolloutRouting();

      const mapTableColumns = (columns: ClientTableColumn[]) => {
        return columns.map((column) => {
          let name = '';

          if (
            defaultClientTableColumnKeys.includes(
              column.fieldId as DefaultClientTableColumnKey
            )
          ) {
            // Column is a default field and can be named via i18n
            name = i18n.t('general.' + column.fieldId);
          } else {
            // Retrieve name from fetchedClientTableCustomFields
            name =
              this.fetchedClientTableCustomFields!.find(
                (customField) => customField._id === column.fieldId
              )?.name ?? '';
          }

          return name
            ? {
                _id: column.fieldId,
                name,
              }
            : [];
        });
      };

      let savedTableColumns: ClientTableColumn[] | undefined;
      if (this.user?.settings?.clientTableColumns) {
        savedTableColumns =
          this.user.settings.clientTableColumns[
            (selectedRollout.value as Rollout)?._id
          ];
      }

      if (savedTableColumns) {
        return mapTableColumns(
          savedTableColumns.sort((a, b) => a.order - b.order)
        ).flat();
      }

      // Filter out `client-user` and `description` as default columns
      const filter: DefaultClientTableColumnKey[] = [
        'client-user',
        'description',
      ];

      return getDefaultClientTableColums(i18n).filter(
        (c) => !filter.includes(c._id)
      );
    },
    async getUser() {
      const accessToken = nuxtStorage.localStorage.getData('accessToken');
      const refreshToken = nuxtStorage.localStorage.getData('refreshToken');
      const hasRememberMe = nuxtStorage.localStorage.getData('rememberMe');

      const user = await fetchCurrentUser();
      this.accessToken = accessToken;
      this.refreshToken = refreshToken;

      if (user) {
        this.user = user;
        return user;
      } else if (hasRememberMe && refreshToken) {
        const response = await refreshAccessToken(refreshToken);

        if (response?.newAccessToken) {
          this.accessToken = response.newAccessToken;
          this.refreshToken = response.newRefreshToken;
          const user = await fetchCurrentUser();

          if (user) {
            this.user = user;
            return user;
          }
        }
      }
    },
    async signOut() {
      this.user = undefined;
      const { $client } = useNuxtApp();
      const accessToken = nuxtStorage.localStorage.getData('accessToken');
      if (accessToken) {
        await $client.auth.logout.query();
      }
      nuxtStorage.localStorage.clear();

      const route = useRoute();
      if (route.name !== 'index') {
        useRouter().push('/?logout-successful=true');
      }
    },
  },
});

export const refreshAccessToken = async (refreshToken: string) => {
  const { $client } = useNuxtApp();

  try {
    const { item } = await $client.auth.refreshAccessToken.mutate({
      refresh_token: refreshToken,
    });
    const {
      access_token: newAccessToken,
      refresh_token: newRefreshToken,
      newRefreshTokenExpireDays,
    } = item;

    nuxtStorage.localStorage.setData('accessToken', newAccessToken, 1, 'd');
    nuxtStorage.localStorage.setData(
      'refreshToken',
      newRefreshToken,
      newRefreshTokenExpireDays ?? sixMonthsInDays,
      'd'
    );

    return { newRefreshToken, newAccessToken };
  } catch (error) {
    useErrorHandler(error, false);
    return null;
  }
};

async function fetchCurrentUser() {
  const { $client } = useNuxtApp();

  try {
    return (await $client.auth.loginByToken.query()).item as User;
  } catch (error) {
    return null;
  }
}
